import * as Sentry from '@sentry/browser';
import { useAuth0 } from '@auth0/auth0-react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom } from 'jotai';
import { Table, TableBody, TableContainer, TableRow, IconButton } from '@mui/material';
import { Order, UsersTableHeadCell } from '@/types';
import { userManagementSearchTerm } from '../state';
import { useDebounce } from '@/hooks/useDebounce';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import { StyledTableCell, TablePaginationContainer } from '../styles';
import { ConfirmationDialog } from '../Dialogs/ConfirmationDialog';
import { UsersTableHead } from '../shared/UsersTableHead';
import {
  useRemoveRoleFromUsersMutation,
  AllRolesDocument,
  UsersByRoleIdDocument,
  useUsersByRoleIdQuery,
  User as UserType,
  AllUsersForAddUserDialogDocument,
  UsersByRoleIdQuery,
} from '@/__generated__/graphql';
import { useRouter } from '@/hooks';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { UserAvatarCell } from '../shared/UserAvatarCell';
import { TableSkeleton } from '../shared/TableSkeleton';
import { TableDataNotFound } from '../shared/TableDataNotFound';
import { TablePagination } from '../../shared/TablePagination';
import { defaultPaginationSize } from '@/constants';

const headCells: UsersTableHeadCell[] = [
  {
    id: 'name',
    numeric: false,
    disablePadding: true,
    label: 'User Name',
  },
  {
    id: 'email',
    numeric: false,
    disablePadding: false,
    label: 'Email Address',
  },
];

type User = UsersByRoleIdQuery['usersByRoleId'][0];

type Props = {
  selectedRoleID: string;
  selectedRoleName: string;
};

export const MembersTable = ({ selectedRoleID, selectedRoleName }: Props) => {
  const currentUser = useAuth0().user;

  const [deleteUser, setDeleteUser] = useState<User | undefined>(undefined);
  const [order, setOrder] = useState<Order>('asc');
  const [orderBy, setOrderBy] = useState<keyof UserType | '_id'>('_id');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [searchValue, setSearchTerm] = useAtom(userManagementSearchTerm);
  const searchTerm = useDebounce(searchValue, 700);

  const router = useRouter();

  useEffect(() => {
    return () => {
      setSearchTerm('');
    };
  }, [setSearchTerm]);

  const { loading, data: userData } = useUsersByRoleIdQuery({
    variables: {
      input: { roleId: selectedRoleID, q: searchTerm },
      pageInfo: {
        skip: page * rowsPerPage,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortDir: order === 'asc' ? 1 : -1,
      },
      usersCountInput: { roleId: selectedRoleID, q: searchTerm },
    },
    skip: !selectedRoleID,
  });

  const { users, usersCount } = useMemo(() => {
    return {
      users: userData?.usersByRoleId ?? [],
      usersCount: userData?.usersCount?.count ?? 0,
    };
  }, [userData]);

  const handleRequestSort = (headerId: keyof UserType) => {
    const isAsc = orderBy === headerId && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(headerId);
  };

  const handleChangePage = (newPage: number) => setPage(newPage);
  const handleChangeRowsPerPage = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleDeleteConfirmationClose = () => {
    setDeleteUser(undefined);
  };

  const handleDeleteConfirmationOpen = (user: User) => {
    setDeleteUser(user);
  };

  const [deleteRoleFromUserMutation] = useRemoveRoleFromUsersMutation();
  const handleRemoveRoleFromUser = useCallback(() => {
    if (deleteUser) {
      deleteRoleFromUserMutation({
        variables: {
          input: {
            roleId: selectedRoleID,
            userIds: [deleteUser.id],
          },
        },
        refetchQueries: [AllRolesDocument, UsersByRoleIdDocument, AllUsersForAddUserDialogDocument],
        onCompleted: (response) => {
          if (response) {
            showSnackBar('The user has been un-assigned from this role', 'success');
          }
        },
        onError: (error) => {
          Sentry.captureMessage(`Failed to un-assign role from user: ${error.message}`, {
            user: { sub: currentUser?.sub },
            extra: { roleId: selectedRoleID, userIds: deleteUser.id },
          });
          showSnackBar(`Failed to un-assign role from user: ${error.message}`, 'error');
        },
      });
      if (users.length === 1 && page > 0) {
        setPage(page - 1);
      }
      handleDeleteConfirmationClose();
    }
  }, [
    currentUser?.sub,
    deleteRoleFromUserMutation,
    deleteUser,
    page,
    selectedRoleID,
    showSnackBar,
    users.length,
  ]);

  const handleUserClick = (id: string) => {
    router.push({
      pathname: `/userManagement/user/${id}`,
    });
  };

  return (
    <>
      <TableContainer sx={{ overflowY: 'auto', mt: 3 }}>
        <Table sx={{ minWidth: 750 }} aria-labelledby="tableTitle">
          {!loading && users.length > 0 && (
            <UsersTableHead
              order={order}
              orderBy={orderBy}
              headCells={headCells}
              onRequestSort={handleRequestSort}
            />
          )}

          <TableBody>
            {loading && <TableSkeleton noOfSimpleColumns={1} />}
            {!loading &&
              users.map((user) => {
                const { id, name, picture, email } = user;
                return (
                  <TableRow hover tabIndex={-1} key={name} onClick={() => handleUserClick(id)}>
                    <StyledTableCell sx={{ width: '300px' }}>
                      <UserAvatarCell name={name} picture={picture} />
                    </StyledTableCell>
                    <StyledTableCell>{email}</StyledTableCell>
                    <StyledTableCell
                      sx={{ width: 72 }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <IconButton>
                        <DeleteOutlineRoundedIcon
                          onClick={() => handleDeleteConfirmationOpen(user)}
                        />
                      </IconButton>
                    </StyledTableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
      {!loading && selectedRoleID && users.length === 0 && (
        <TableDataNotFound message="No Users Found" />
      )}
      {!loading && usersCount > 0 && (
        <TablePaginationContainer>
          <TablePagination
            count={usersCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[...defaultPaginationSize, { label: 'All', value: usersCount }]}
          />
        </TablePaginationContainer>
      )}
      <ConfirmationDialog
        open={!!deleteUser}
        title="Un-assigning role"
        message={`Are you sure you want to remove ${selectedRoleName} role from ${deleteUser?.name}? User will no longer have the permissions of this role.`}
        buttonText="Un-assign role"
        handleRemoveUserFromGroup={handleRemoveRoleFromUser}
        onClose={handleDeleteConfirmationClose}
      />
    </>
  );
};
